/**
 * Fetches and downloads protected files via Bearer token.
 *
 * Backend routes /api/v1/files/{id} and /api/v1/portal/files/{id} require an
 * Authorization header. `<img src>` and `<a href>` tags don't send it, so we
 * fetch as blob and mint an object-URL for the browser to display or
 * download. Never expose raw URLs / query-token shortcuts.
 */
import { API_BASE, getToken, formatError } from "@/lib/api";
import { toast } from "sonner";

async function _fetchBlob(path) {
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      detail = j?.detail || detail;
    } catch { /* not JSON */ }
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  return await res.blob();
}

export async function fetchProtectedBlobUrl(path) {
  const blob = await _fetchBlob(path);
  return URL.createObjectURL(blob);
}

export async function openProtectedFile(path, errorLabel = "Could not open file") {
  try {
    const url = await fetchProtectedBlobUrl(path);
    window.open(url, "_blank", "noopener");
    // Free memory after the tab has a chance to load
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    toast.error(formatError(e, errorLabel));
  }
}

export async function downloadProtectedFile(path, filename,
                                            errorLabel = "Could not download file") {
  try {
    const blob = await _fetchBlob(path);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "file";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  } catch (e) {
    toast.error(formatError(e, errorLabel));
  }
}
